import React from 'react'
import { Snowflake, Caravan } from 'lucide-react'
import { ContainerTask } from '../../types/container'

/**
 * 「这一单要哪种发电机」的那个标记，跟在底盘号后面。
 *
 * 为什么是一个共用组件而不是各画各的
 * ---------------------------------
 * 这个标记要同时长在派单板的任务行和司机卡上（司机卡那张又长在拖拽排活和派单
 * 确认窗口里）。上一版是两处各写一遍，结果第一版只做了任务行、漏了司机卡 ——
 * 而派活恰恰是在司机卡上做的。两份各自维护就会再漏一次，所以合成一处。
 *
 * 为什么是两种图标
 * ---------------
 * 客户点名的是两种不同的东西，司机要跑的路也不一样：
 *
 *   Genset          一台独立的发电机，去堆场单独提、用完单独还，有租金、要加油。
 *                   这一趟因此**多出两脚活**。画一朵雪花 —— 它是一台独立的设备。
 *   Genset Chassis  发电机焊在底盘上，是底盘的一种型号，没有额外的提还。
 *                   画一节拖车厢，而且就贴在底盘号后面 —— 位置本身在说
 *                   「说的是这台底盘」。
 *
 * 只有这两种会画。'none'（明确不要）和 undefined（没人说过）什么都不画 ——
 * 一个「谁都有」的图标等于没有图标。
 */

const MARKS = {
  genset: {
    Icon: Snowflake,
    title: 'Genset — a standalone unit: separate pick-up and return at the yard, needs fuel on the road',
    label: 'Genset required',
  },
  genset_chassis: {
    Icon: Caravan,
    title: 'Genset Chassis — generator mounted on the chassis; no extra pick-up or return',
    label: 'Genset chassis required',
  },
} as const

interface GensetMarkProps {
  gensetType: ContainerTask['gensetType']
  /** 司机卡那张在一行 flex 里，不能被挤扁。 */
  className?: string
}

export function GensetMark({ gensetType, className = '' }: GensetMarkProps) {
  if (gensetType !== 'genset' && gensetType !== 'genset_chassis') return null

  const { Icon, title, label } = MARKS[gensetType]

  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded bg-cyan-100 text-cyan-700 ${className}`}
      title={title}
      aria-label={label}
    >
      <Icon className="w-2.5 h-2.5" />
    </span>
  )
}

export default GensetMark
